import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetShip } from "../../App/Context"
import { EngineMissingAdvice } from "./EngineMissingAdvice"
import { Ship } from "../../App/Ship"
import { BlockField, BlockFieldInputs, BlockRow } from "../../Components/Block"
import { UserAdviceDialog } from "../../Components/UserAdvice/UserAdvice"
import { OverlayViewDefinition } from "../OverlayView"
import { IEngineHintAnswer, IEngineHintSince, IShipDataEngine, IShipVesselType } from "../../Types"
import { logger } from "../../App/Logger"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceEngineMissingViewProps } from "./Params"

// The crew's answer to the "engine entry missing?" hint (#1227) — the mirror image of
// `UserAdviceStopView`, which already offers "stop engines" for the manual case.
//
// Two things about this view are load-bearing:
//
//  1. **It writes nothing itself.** Both answers go out as the single arbitrated
//     `current/engineHint` command. Several devices can be showing this same dialog, and
//     if each composed its own engine/status writes they would race into a contradictory
//     logbook — two "started" answers with different anchors open two competing engine
//     statuses, and "started" beside "we're sailing" leaves an open status next to an
//     "engine off" marker. The primary re-evaluates the hint before acting, so the first
//     answer wins and the rest are no-ops.
//  2. **The anchor is an explicit choice.** Back-dating to the trigger is the default
//     (that is the forgotten time the feature exists to recover), but a late answer must
//     be able to say "from now" rather than invent hours of engine time.
export function UserAdviceEngineMissingView({ advice, state }: UserAdviceEngineMissingViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const t = app.getT("UserAdviceEngineMissingView");
  const parent = useMemo(() => advice.parent as EngineMissingAdvice, [advice]);

  const engines = useMemo(() => (ship ? Ship.engines(ship) : []), [ship]);
  const severalEngines = engines.length > 1;
  const [selectedEngines, setSelectedEngines] = useState<string[]>([]);
  const [since, setSince] = useState<IEngineHintSince>(IEngineHintSince.trigger);

  // A motor vessel has no sails, so "no, we're sailing" is nonsense there — but the
  // answer itself must stay available (@mutle decision): the hint is only resolved by an
  // open engine status or an "engine off" marker, so removing it would leave a motor
  // vessel re-broadcasting the ghost every tick with a falsehood as the only way out.
  // Gated on `vesselType`, never on the hint's `reason`: a motor vessel leaving a berth
  // reports `castOff`, so a reason-gate would mislabel most motor-vessel departures.
  const noSails = ship?.vesselType == IShipVesselType.motor;

  const toggleEngine = useCallback((id: string) => {
    setSelectedEngines((current) => current.includes(id) ? current.filter((e) => e != id) : [...current, id]);
  }, []);

  // Nothing is pre-ticked, so an engine is only ever recorded because the crew said so.
  // This restores the one-tap common case (both engines running while manoeuvring)
  // without assuming it.
  const toggleAllEngines = useCallback(() => {
    setSelectedEngines((current) => current.length == engines.length ? [] : engines.map((e: IShipDataEngine) => e.id));
  }, [engines]);

  const answer = useCallback(async (value: IEngineHintAnswer) => {
    logger.debug("engine hint", value, since, selectedEngines);
    state.setSelected(undefined);
    await UserAdviceViewsData.engineHint(app.apiClient, {
      answer: value,
      engines: value == IEngineHintAnswer.started && severalEngines ? selectedEngines : undefined,
      since: value == IEngineHintAnswer.started ? since : undefined,
    });
  }, [app, since, selectedEngines, severalEngines, state]);

  // On a multi-engine ship the crew must name at least one engine before confirming.
  const startDisabled = severalEngines && selectedEngines.length == 0;

  return (
    <UserAdviceDialog className="UserAdviceEngineMissingView">
      <BlockRow className="block-row-infotext">
        {t(`text_${parent.reason}`)}
      </BlockRow>

      {severalEngines && (
        <BlockRow>
          <BlockField className="block-row-checkboxes">
            <BlockFieldInputs>
              {engines.map((engine: IShipDataEngine) => (
                <BlockField label={Ship.engineName(ship, engine.id, engine.number)} labelAfter={true} labelFor={`engineHintEngine-${engine.id}`} key={engine.id}>
                  <input checked={selectedEngines.includes(engine.id)} id={`engineHintEngine-${engine.id}`} name="engines" type="checkbox" value={engine.id} onChange={() => toggleEngine(engine.id)} />
                </BlockField>
              ))}
              <BlockField label={t("all_engines")} labelAfter={true} labelFor="engineHintAllEngines">
                <input checked={selectedEngines.length == engines.length} id="engineHintAllEngines" name="allEngines" type="checkbox" onChange={toggleAllEngines} />
              </BlockField>
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
      )}

      <BlockRow>
        <BlockField className="block-row-checkboxes">
          <BlockFieldInputs className="block_field_small_24">
            <BlockField className="checkbox-text-bigger" label={t("since_trigger", { time: app.format("time", parent.since) })} labelAfter={true} labelFor={`engineHintSince-${IEngineHintSince.trigger}`}>
              <input checked={since == IEngineHintSince.trigger} id={`engineHintSince-${IEngineHintSince.trigger}`} name="since" type="radio" value={IEngineHintSince.trigger} onChange={() => setSince(IEngineHintSince.trigger)} />
            </BlockField>
            <BlockField className="checkbox-text-bigger" label={t("since_now")} labelAfter={true} labelFor={`engineHintSince-${IEngineHintSince.now}`}>
              <input checked={since == IEngineHintSince.now} id={`engineHintSince-${IEngineHintSince.now}`} name="since" type="radio" value={IEngineHintSince.now} onChange={() => setSince(IEngineHintSince.now)} />
            </BlockField>
          </BlockFieldInputs>
        </BlockField>
      </BlockRow>

      <BlockRow actions={true} className="bottom">
        <BlockField><button className="button" onClick={() => state.setSelected(undefined)}>{t("cancel")}</button></BlockField>
        <BlockField><button className="button" onClick={() => answer(IEngineHintAnswer.sailing)}>{t(noSails ? "sailing_noSails" : "sailing")}</button></BlockField>
        <BlockField><button className="button default" disabled={startDisabled} onClick={() => answer(IEngineHintAnswer.started)}>{t("started")}</button></BlockField>
      </BlockRow>
    </UserAdviceDialog>
  );
}

// This dialog's own overlay attributes (#1171), declared here rather than in
// `UserAdviceOverlayView` so the dialog keeps deciding how it presents itself. It
// takes its title straight from the advice whose button opened it, and stays
// closable unless that advice asked otherwise. Deliberately hook-free -- one of
// these is called per open advice, so a hook would vary React's hook count.
export function userAdviceEngineMissingOverlay(props: UserAdviceEngineMissingViewProps): OverlayViewDefinition {
  return {
    close: !props.advice.props.hideClose,
    title: props.advice.props.title,
    render: () => <UserAdviceEngineMissingView {...props} />,
  };
}
